import React, { Fragment, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Menu, Transition } from '@headlessui/react';
import { Mixpanel } from '../../../services/Mixpanel';

// actions: [{label: string, icon: React.ReactNode, onClick: () => {}}]
// OR -- buttons: [React.ReactNode]
// direction: 'left' or 'right'

export const THREE_DOT_MENU_BUTTON_CLASS = 'three-dot-menu-button';
export const ThreeDotMenu = ({
  tutorialId,
  actions,
  buttons,
  direction = 'left',
  className = '',
  buttonClassName = '',
  location,
  onMenuToggled,
  disabled = false,
  customTrackFn = null,
  allowMultipleClicks = false,
  mixpanelTrackEvent = 'Three Dot Menu Clicked',
  buttonIcon = <EllipsisVerticalIcon className='w-4 h-4' />,
  ariaLabel = 'Three Dot Menu',
  ...buttonProps
}) => {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [openUpward, setOpenUpward] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const lastScrollY = useRef(0);
  const closeMenuRef = useRef(null);
  let trackEvent = Mixpanel.track;
  if (customTrackFn) {
    trackEvent = customTrackFn;
  }

  // Notify parent of menu toggle state changes via useEffect to avoid setState during render
  useEffect(() => {
    onMenuToggled?.(isOpen);
  }, [isOpen, onMenuToggled]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handleScroll = (e) => {
      if (!closeMenuRef.current?.close) {
        return;
      }
      // Get the scroll position from either the window or the scrolling element
      const currentScroll = e.target === document ? window.scrollY : e.target.scrollTop;
      const scrollDiff = Math.abs(currentScroll - lastScrollY.current);

      if (scrollDiff > 3 && closeMenuRef.current) {
        closeMenuRef.current?.close?.();
      }
    };

    // Listen for window scroll
    window.addEventListener('scroll', handleScroll);

    // Find and listen to all scrollable containers
    const scrollableContainers = document.querySelectorAll(
      '.overflow-auto, .overflow-y-auto, .overflow-scroll'
    );
    scrollableContainers.forEach((container) => {
      container.addEventListener('scroll', handleScroll);
    });

    if (!closeMenuRef.current?.close) {
      // remove the event listeners when the menu is closed
      window.removeEventListener('scroll', handleScroll);
      scrollableContainers.forEach((container) => {
        container.removeEventListener('scroll', handleScroll);
      });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollableContainers.forEach((container) => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, [closeMenuRef.current?.close]);

  const calculateMenuPosition = (element) => {
    const rect = element.getBoundingClientRect();

    // Estimate menu height (4 items * ~40px each + padding)
    // This is a reasonable estimate; actual height may vary
    const estimatedMenuHeight = 200;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Determine if we should open upward
    const shouldOpenUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
    setOpenUpward(shouldOpenUpward);

    setMenuPosition({
      top: shouldOpenUpward ? rect.top + window.scrollY : rect.bottom + window.scrollY,
      left: direction === 'left' ? rect.left : rect.right,
    });

    // Find the closest scrollable parent
    let scrollElement = element;
    while (scrollElement && scrollElement !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(scrollElement);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        lastScrollY.current = scrollElement.scrollTop;
        break;
      }
      scrollElement = scrollElement.parentElement;
    }
    // If no scrollable parent found, use window scroll position
    if (!scrollElement || scrollElement === document.body) {
      lastScrollY.current = window.scrollY;
    }
  };

  return (
    <Menu
      data-no-dnd='true'
      as='div'
      className={`relative flex items-center ${className}`}
      {...buttonProps}>
      {({ open, close }) => {
        // Store the close function in the ref when menu state changes
        closeMenuRef.current = { open, close };

        // Track open state for useEffect-based notification (avoid setState during render)
        if (open !== isOpen) {
          // Schedule state update for after render
          setTimeout(() => setIsOpen(open), 0);
        }

        return (
          <div data-component-type='three-dot-menu-contents'>
            <Menu.Button
              data-tutorial-id={tutorialId}
              aria-label={ariaLabel}
              data-location={location}
              data-menu-button={true}
              className={`flex items-center text-gray-500 w-full rounded-lg ${
                disabled ? 'opacity-50 pointer-events-none' : ''
              } ${buttonClassName ? buttonClassName : 'p-1.5 hover:bg-gray-200'}`}
              onClick={(e) => {
                if (disabled) {
                  return;
                }
                e.stopPropagation();
                trackEvent(mixpanelTrackEvent, { location });
                calculateMenuPosition(e.currentTarget);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  trackEvent(mixpanelTrackEvent, { location });
                  calculateMenuPosition(e.currentTarget);
                }
              }}>
              {buttonIcon}
            </Menu.Button>
            {mounted &&
              createPortal(
                <div className='absolute inset-0 z-50 pointer-events-none text-sm'>
                  <div
                    style={{
                      position: 'absolute',
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                      transform: `${direction === 'right' ? 'translateX(-100%)' : ''}${openUpward ? ' translateY(-100%)' : ''}`.trim() || 'none',
                    }}
                    className='pointer-events-auto'
                    onPointerDown={(e) => {
                      // stop propagation to prevent Sortable component (if it exists in parent) from taking over
                      e.stopPropagation();
                    }}>
                    <Transition
                      as={Fragment}
                      enter='transition ease-out duration-100'
                      enterFrom='transform opacity-0 scale-95'
                      enterTo='transform opacity-100 scale-100'
                      leave='transition ease-in duration-75'
                      leaveFrom='transform opacity-100 scale-100'
                      leaveTo='transform opacity-0 scale-95'>
                      <Menu.Items className='whitespace-nowrap rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
                        {actions?.length > 0 &&
                          actions.map((action) => {
                            return (
                              <Menu.Item key={action.label}>
                                {({ active }) => (
                                  <button
                                    data-menu-button={true}
                                    className={`${
                                      active ? 'bg-gray-100' : ''
                                    } flex w-full items-center px-4 py-2 text-gray-700`}
                                    onClick={(e) => {
                                      if (allowMultipleClicks) {
                                        e.preventDefault(); // Prevent the default menu closing
                                        e.stopPropagation();
                                      }
                                      action.onClick(e);
                                    }}>
                                    {action.icon && <div className='me-2'>{action.icon}</div>}
                                    {action.label}
                                  </button>
                                )}
                              </Menu.Item>
                            );
                          })}
                        {buttons &&
                          buttons.map((Button, index) => (
                            <Menu.Item key={index} data-menu-button={true}>
                              {({ active }) => (
                                <div className={active ? 'bg-gray-100' : ''}>{Button}</div>
                              )}
                            </Menu.Item>
                          ))}
                      </Menu.Items>
                    </Transition>
                  </div>
                </div>,
                document.body
              )}
          </div>
        );
      }}
    </Menu>
  );
};
